import * as cases from './cases';
import * as clothes from './clothes';
import * as currency from './currency';
import * as fish from './fish';
import * as helpModals from './helpModals';
import * as items from './items';
import * as kits from './kits';
import * as rims from './rims';
import * as vehicles from './vehicles';

// Предзагрузка всех изображений одежды через import.meta.glob
const clothesImages = import.meta.glob<string>('./clothes/**/*.png', {
	eager: false,
	import: 'default',
});

export type Size = {
	w: number;
	h: number;
};

export type Collection = {
	sizes: Record<string, Size>;
	contents: readonly string[];
	extension: string | null;
	// содержит ли коллекция картинки разных размеров
	useSizes: boolean;
};

export const COLLECTIONS: Record<string, Collection> = {
	vehicles: {
		sizes: vehicles.PRESET_SIZES,
		contents: vehicles.CONTENTS,
		extension: vehicles.EXTENSION,
		useSizes: true,
	},
	items: {
		sizes: items.PRESET_SIZES,
		contents: items.CONTENTS,
		extension: items.EXTENSION,
		useSizes: false,
	},
	currency: {
		sizes: currency.PRESET_SIZES,
		contents: currency.CONTENTS,
		extension: null,
		useSizes: true,
	},
	clothes: {
		sizes: clothes.PRESET_SIZES,
		contents: clothes.CONTENTS,
		extension: clothes.EXTENSION,
		useSizes: false,
	},
	fish: {
		sizes: fish.PRESET_SIZES,
		contents: fish.CONTENTS,
		extension: fish.EXTENSION,
		useSizes: false,
	},
	helpModals: {
		sizes: helpModals.PRESET_SIZES,
		contents: helpModals.CONTENTS,
		extension: helpModals.EXTENSION,
		useSizes: false,
	},
	cases: {
		sizes: cases.PRESET_SIZES,
		contents: cases.CONTENTS,
		extension: cases.EXTENSION,
		useSizes: false,
	},
	kits: {
		sizes: kits.PRESET_SIZES,
		contents: kits.CONTENTS,
		extension: kits.EXTENSION,
		useSizes: false,
	},
	rims: {
		sizes: rims.PRESET_SIZES,
		contents: rims.CONTENTS,
		extension: rims.EXTENSION,
		useSizes: false,
	},
};

export type CollectionKey = keyof typeof COLLECTIONS;
export type SizeKey<T extends CollectionKey> =
	keyof (typeof COLLECTIONS)[T]['sizes'];

const failedImagePaths = new Set<string>();
const imageLoadCache = new Map<string, Promise<string>>();

export const resolveImage = (imageSrc: string): Promise<string> => {
	const cachedPromise = imageLoadCache.get(imageSrc);
	if (cachedPromise) {
		return cachedPromise;
	}

	const loadPromise = new Promise<string>((resolve, reject) => {
		const img = new Image();
		img.src = imageSrc;

		img.onload = () => {
			resolve(imageSrc);
		};

		img.onerror = () => {
			reject(new Error(`Failed to load image: ${imageSrc}`));
		};
	});

	imageLoadCache.set(imageSrc, loadPromise);

	return loadPromise;
};

export async function getImageById(
	collection: CollectionKey,
	id: string,
	size: string,
): Promise<string> {
	const extension = COLLECTIONS[collection].extension;

	const cacheKey = `${collection}:${id}:${size}`;

	if (failedImagePaths.has(cacheKey)) {
		return '';
	}

	try {
		if (collection === 'clothes') {
			const imagePath = clothes.getClothesImagePathFromMap(id);
			if (!imagePath) {
				throw new Error(`Clothes ID not found in map: ${id}`);
			}

			const imageKey = `./clothes/${imagePath}.${clothes.EXTENSION}`;
			const imageLoader = clothesImages[imageKey];

			if (!imageLoader) {
				throw new Error(`Clothes image not found: ${imageKey}`);
			}

			const imageUrl = await imageLoader();
			return resolveImage(imageUrl);
		}

		if (!COLLECTIONS[collection].useSizes) {
			const response = await import(`./${collection}/${id}.${extension}`);
			return resolveImage(response.default);
		}

		const response = await import(`./${collection}/${size}/${id}.${extension}`);
		return resolveImage(response.default);
	} catch (error) {
		failedImagePaths.add(cacheKey);
		return '';
	}
}

export function getCollection(collection: CollectionKey) {
	return COLLECTIONS[collection];
}

export const findNearestLargerSize = (
	collection: CollectionKey,
	targetSize: number,
) => {
	const sizes = Object.entries(COLLECTIONS[collection].sizes);
	const sortedSizes = sizes.sort((a, b) => a[1].h - b[1].h);
	const nearestLargerSize = sortedSizes.find(
		// @ts-ignore
		([_, size]) => size.h >= targetSize,
	);

	return nearestLargerSize
		? nearestLargerSize[0]
		: Object.keys(COLLECTIONS[collection].sizes).at(-1);
};

export const calculateProportionalSize = <T extends CollectionKey>(
	collection: T,
	nearestSize: SizeKey<T>,
	customHeight: number,
): Size => {
	const originalSize = COLLECTIONS[collection].sizes[nearestSize];
	const aspectRatio = originalSize.w / originalSize.h;
	const proportionalWidth = Math.round(customHeight * aspectRatio);

	return {
		w: proportionalWidth,
		h: customHeight,
	};
};

export { clothes };
